import React, { useState, useCallback, useEffect } from 'react';
import { useToast } from '@/hooks/use-toast';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Checkbox } from '@/components/ui/checkbox';
import { Alert, AlertDescription, AlertTitle } from '@/components/ui/alert';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
} from '@/components/ui/dialog';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from '@/components/ui/popover';
import { Calendar } from '@/components/ui/calendar';
import { Loader2, ExternalLink, CalendarIcon, RotateCcw } from 'lucide-react';
import { format } from 'date-fns';
import { cn } from '@/lib/utils';
import { getConnectorFriendlyName } from '@/utils/connectorNames';
import {
  SYNC_FREQUENCY_OPTIONS,
  estToPacific,
  pacificToEst,
  SchemaEntry,
  computeDefaultSchemaChanges,
  computeNewTables,
  extractTableLists,
} from '@/utils/connectionSettingsUtils';
import {
  type ConnectionDetails,
  type ConnectionTableState,
  type SyncStartDateOrigin,
  getConnectionDetails,
  getConnectionSchemas,
  getConnectionTableState,
  getConnectorDefaultTables,
  getCsvLastDate,
  resetSyncStartDate,
  updateConnectionSchema,
  updateConnectionTableState,
  updateSyncSettings,
  updateSyncStartDate,
} from '@/utils/connectionSettingsApi';
import { FivetranRescheduledSyncNotice } from '@/components/settings/FivetranRescheduledSyncNotice';

interface OrgConnectionSettingsDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  connectorId: string;
  connectionName: string;
  service: string;
  orgId: string;
  orgConnections: any;
}

const OrgConnectionSettingsDialog: React.FC<OrgConnectionSettingsDialogProps> = ({
  open,
  onOpenChange,
  connectorId,
  connectionName,
  service,
  orgId,
  orgConnections,
}) => {
  const { toast } = useToast();

  // Details state
  const [details, setDetails] = useState<ConnectionDetails | null>(null);
  const [loadingDetails, setLoadingDetails] = useState(false);

  // Sync frequency
  const [editFrequency, setEditFrequency] = useState<string>('');
  const [editDailySyncTime, setEditDailySyncTime] = useState<string>('');
  const [savingSettings, setSavingSettings] = useState(false);

  // Schema state
  const [schemaChanges, setSchemaChanges] = useState<Record<string, SchemaEntry>>({});
  const [loadingSchema, setLoadingSchema] = useState(false);
  const [savingSchema, setSavingSchema] = useState(false);
  const [connectorDefaults, setConnectorDefaults] = useState<string[] | null>(null);
  const [loadingDefaults, setLoadingDefaults] = useState(false);

  // Per-connection table state
  // 'loading' = fetching, null = no DB record yet, 'error' = fetch failed, object = loaded
  const [connectionTableState, setConnectionTableState] = useState<
    ConnectionTableState | null | 'loading' | 'error'
  >('loading');

  // Sync start date state
  const [csvLastDate, setCsvLastDate] = useState<string | null>(null);
  const [loadingCsvDate, setLoadingCsvDate] = useState(false);
  const [manualDate, setManualDate] = useState<Date | undefined>(undefined);
  const [savingSyncDate, setSavingSyncDate] = useState(false);

  // Check if org has an Amazon CSV connection
  const hasAmazonCsv = (() => {
    if (!orgConnections) return false;
    const conns = Array.isArray(orgConnections) ? orgConnections : Object.values(orgConnections);
    return conns.some((c: any) =>
      c.ecommerce_subtype === 'amazon' ||
      (c.import_type === 'ecommerce' && c.ecommerce_subtype === 'amazon')
    );
  })();

  const isAmazonService = service?.toLowerCase().includes('amazon') || service === 'amazon_selling_partner';

  const fetchDetails = useCallback(async () => {
    setLoadingDetails(true);
    try {
      const data = await getConnectionDetails(connectorId, orgId);
      setDetails(data);
      setEditFrequency(String(data.sync_frequency || 360));
      setEditDailySyncTime(estToPacific(data.daily_sync_time || '03:00'));
      if (data.sync_start_date) {
        setManualDate(new Date(data.sync_start_date));
      }
    } catch (err) {
      console.error('Failed to fetch details:', err);
      toast({ title: 'Error', description: 'Failed to fetch connection details', variant: 'destructive' });
    } finally {
      setLoadingDetails(false);
    }
  }, [connectorId, orgId, toast]);

  const fetchSchema = useCallback(async () => {
    setLoadingSchema(true);
    try {
      const schemaData = await getConnectionSchemas(connectorId);
      setSchemaChanges(JSON.parse(JSON.stringify(schemaData)));
    } catch (err) {
      console.error('Failed to fetch schema:', err);
      toast({ title: 'Error', description: 'Failed to fetch schema config', variant: 'destructive' });
    } finally {
      setLoadingSchema(false);
    }
  }, [connectorId, toast]);

  const fetchConnectorDefaults = useCallback(async () => {
    setLoadingDefaults(true);
    try {
      setConnectorDefaults(await getConnectorDefaultTables(service));
    } catch {
      setConnectorDefaults([]);
    } finally {
      setLoadingDefaults(false);
    }
  }, [service]);

  const fetchConnectionTableState = useCallback(async () => {
    setConnectionTableState('loading');
    try {
      setConnectionTableState(await getConnectionTableState(connectorId, orgId));
    } catch {
      setConnectionTableState('error');
    }
  }, [connectorId, orgId]);

  const saveConnectionTableState = useCallback(async (allowedTables: string[], disabledTables: string[]) => {
    try {
      await updateConnectionTableState(connectorId, orgId, allowedTables, disabledTables);
    } catch (err) {
      console.error('Failed to save connection table state:', err);
    }
  }, [connectorId, orgId]);

  useEffect(() => {
    if (open && connectorId) {
      setSchemaChanges({});
      fetchDetails();
      fetchSchema();
      fetchConnectorDefaults();
      fetchConnectionTableState();
    }
  }, [open, connectorId, fetchDetails, fetchSchema, fetchConnectorDefaults, fetchConnectionTableState]);

  // Lazy initialization: on first dialog open (no stored state), snapshot the current schema silently
  useEffect(() => {
    if (connectionTableState !== null || Object.keys(schemaChanges).length === 0) return;
    const { allowedTables, disabledTables } = extractTableLists(schemaChanges);
    saveConnectionTableState(allowedTables, disabledTables);
    setConnectionTableState({ allowed_tables: allowedTables, disabled_tables: disabledTables });
  }, [connectionTableState, schemaChanges, saveConnectionTableState]);

  const handleSaveSettings = async () => {
    setSavingSettings(true);
    try {
      await updateSyncSettings(
        connectorId,
        parseInt(editFrequency),
        editFrequency === '1440' ? pacificToEst(editDailySyncTime) : undefined,
      );
      toast({ title: 'Success', description: 'Sync settings updated' });
      fetchDetails();
    } catch (err) {
      console.error('Failed to save settings:', err);
      toast({ title: 'Error', description: 'Failed to update sync settings', variant: 'destructive' });
    } finally {
      setSavingSettings(false);
    }
  };

  const handleSaveSchema = async () => {
    setSavingSchema(true);
    try {
      await updateConnectionSchema(connectorId, schemaChanges);
      toast({ title: 'Success', description: 'Schema config updated' });

      // Persist the updated per-connection table state so new tables
      // the user just disabled no longer appear in future warnings.
      const { allowedTables, disabledTables } = extractTableLists(schemaChanges);
      await saveConnectionTableState(allowedTables, disabledTables);
      setConnectionTableState({ allowed_tables: allowedTables, disabled_tables: disabledTables });

      fetchSchema();
    } catch (err) {
      console.error('Failed to save schema:', err);
      toast({ title: 'Error', description: 'Failed to update schema config', variant: 'destructive' });
    } finally {
      setSavingSchema(false);
    }
  };

  const handleFetchCsvDate = async () => {
    setLoadingCsvDate(true);
    try {
      const lastDate = await getCsvLastDate(orgId);
      if (lastDate) {
        setCsvLastDate(lastDate);
        toast({ title: 'CSV Date Found', description: `Last transaction date: ${lastDate}` });
      } else {
        toast({ title: 'No Date Found', description: 'No transaction dates found in CSV data', variant: 'destructive' });
      }
    } catch (err) {
      console.error('Failed to fetch CSV date:', err);
      toast({ title: 'Error', description: 'Failed to fetch CSV last date', variant: 'destructive' });
    } finally {
      setLoadingCsvDate(false);
    }
  };

  const handleSetSyncStartDate = async (dateStr: string, origin: SyncStartDateOrigin) => {
    setSavingSyncDate(true);
    try {
      await updateSyncStartDate(connectorId, orgId, dateStr, origin);
      toast({ title: 'Success', description: `Sync start date set to ${dateStr} (${origin})` });
      await fetchDetails();
      setSavingSyncDate(false);
    } catch (err) {
      console.error('Failed to set sync start date:', err);
      toast({ title: 'Error', description: 'Failed to update sync start date', variant: 'destructive' });
      setSavingSyncDate(false);
    }
  };

  const handleResetSyncDate = async () => {
    setSavingSyncDate(true);
    try {
      await resetSyncStartDate(connectorId, orgId);
      toast({ title: 'Success', description: 'Sync start date reset — Fivetran will sync all history' });
      setManualDate(undefined);
      setCsvLastDate(null);
      await fetchDetails();
      setSavingSyncDate(false);
    } catch (err) {
      console.error('Failed to reset sync start date:', err);
      toast({ title: 'Error', description: 'Failed to reset sync start date', variant: 'destructive' });
      setSavingSyncDate(false);
    }
  };

  const toggleTableEnabled = (schemaName: string, tableName: string) => {
    setSchemaChanges((prev) => ({
      ...prev,
      [schemaName]: {
        ...prev[schemaName],
        tables: {
          ...prev[schemaName]?.tables,
          [tableName]: {
            ...prev[schemaName]?.tables?.[tableName],
            enabled: !prev[schemaName]?.tables?.[tableName]?.enabled,
          },
        },
      },
    }));
  };

  const handleResetToDefaults = () => {
    if (Object.keys(schemaChanges).length === 0) return;
    setSchemaChanges(computeDefaultSchemaChanges(schemaChanges, connectorDefaults ?? []));
  };

  const newlyDetectedTables =
    connectionTableState && connectionTableState !== 'loading' && connectionTableState !== 'error'
      ? computeNewTables(schemaChanges, connectionTableState.allowed_tables, connectionTableState.disabled_tables)
      : [];

  const displayName = getConnectorFriendlyName(service) || connectionName;

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-2xl max-h-[80vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle>{displayName} — Connection Settings</DialogTitle>
          <DialogDescription asChild>
            <a
              href={`https://fivetran.com/dashboard/connections/${connectorId}`}
              target="_blank"
              rel="noopener noreferrer"
              className="inline-flex items-center gap-1 text-sm text-primary hover:underline"
            >
              Open in Fivetran <ExternalLink className="h-3.5 w-3.5" />
            </a>
          </DialogDescription>
        </DialogHeader>

        {/* Sync Settings */}
        <Card>
          <CardHeader className="pb-3">
            <CardTitle className="text-base">Sync Settings</CardTitle>
          </CardHeader>
          <CardContent className="space-y-4">
            {loadingDetails ? (
              <div className="flex items-center gap-2 text-muted-foreground">
                <Loader2 className="h-4 w-4 animate-spin" /> Loading...
              </div>
            ) : details ? (
              <>
                <div className="flex items-center gap-2 text-sm">
                  <span className="text-muted-foreground">Historical sync:</span>
                  <Badge variant={details.is_historical_sync ? 'secondary' : 'default'}>
                    {details.is_historical_sync ? 'In Progress' : 'Complete'}
                  </Badge>
                </div>

                <FivetranRescheduledSyncNotice
                  sync_state={details.sync_state}
                  rescheduled_for={details.rescheduled_for}
                  sync_tasks={details.sync_tasks}
                  sync_warnings={details.sync_warnings}
                  status_reason={details.status_reason}
                />

                <div className="flex items-center gap-4">
                  <div className="space-y-1 flex-1">
                    <label className="text-sm font-medium">Sync Frequency</label>
                    <Select value={editFrequency} onValueChange={setEditFrequency}>
                      <SelectTrigger>
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent>
                        {SYNC_FREQUENCY_OPTIONS.map((opt) => (
                          <SelectItem key={opt.value} value={opt.value}>
                            {opt.label}
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>

                  {editFrequency === '1440' && (
                    <div className="space-y-1">
                      <label className="text-sm font-medium">Daily Sync Time (Pacific)</label>
                      <div className="flex items-center gap-1">
                        <Select
                          value={editDailySyncTime.split(':')[0] || '00'}
                          onValueChange={(h) => {
                            const mins = editDailySyncTime.split(':')[1] || '00';
                            setEditDailySyncTime(`${h}:${mins}`);
                          }}
                        >
                          <SelectTrigger className="w-20">
                            <SelectValue />
                          </SelectTrigger>
                          <SelectContent className="bg-background z-50">
                            {Array.from({ length: 24 }, (_, i) => String(i).padStart(2, '0')).map((h) => (
                              <SelectItem key={h} value={h}>{h}</SelectItem>
                            ))}
                          </SelectContent>
                        </Select>
                        <span className="text-sm font-medium">:</span>
                        <Select
                          value={editDailySyncTime.split(':')[1] || '00'}
                          onValueChange={(m) => {
                            const hrs = editDailySyncTime.split(':')[0] || '00';
                            setEditDailySyncTime(`${hrs}:${m}`);
                          }}
                        >
                          <SelectTrigger className="w-20">
                            <SelectValue />
                          </SelectTrigger>
                          <SelectContent className="bg-background z-50">
                            {['00', '30'].map((m) => (
                              <SelectItem key={m} value={m}>{m}</SelectItem>
                            ))}
                          </SelectContent>
                        </Select>
                      </div>
                    </div>
                  )}
                </div>

                <Button onClick={handleSaveSettings} disabled={savingSettings} size="sm">
                  {savingSettings && <Loader2 className="mr-2 h-4 w-4 animate-spin" />}
                  Save Sync Settings
                </Button>
              </>
            ) : (
              <p className="text-sm text-muted-foreground">Failed to load details</p>
            )}
          </CardContent>
        </Card>

        {/* Sync Start Date — only for Amazon connections with CSV data */}
        {isAmazonService && (
          <Card>
            <CardHeader className="pb-3">
              <CardTitle className="text-base">Sync Start Date</CardTitle>
            </CardHeader>
            <CardContent className="space-y-4 relative">
              {savingSyncDate && (
                <div className="absolute inset-0 bg-background/60 backdrop-blur-[1px] z-10 flex items-center justify-center rounded-md">
                  <div className="flex items-center gap-2 text-sm text-muted-foreground">
                    <Loader2 className="h-4 w-4 animate-spin" /> Updating sync date...
                  </div>
                </div>
              )}
              {details?.sync_start_date ? (
                <div className="text-sm space-y-1">
                  <div className="flex items-center gap-2">
                    <span className="text-muted-foreground">Current sync start date:</span>
                    <Badge variant="outline">{details.sync_start_date}</Badge>
                  </div>
                  <div className="flex items-center gap-2">
                    <span className="text-muted-foreground">Origin:</span>
                    <Badge variant={details.sync_start_date_origin === 'csv' ? 'secondary' : 'default'}>
                      {details.sync_start_date_origin === 'csv' ? 'From CSV' : 'Manual override'}
                    </Badge>
                  </div>
                  {details.sync_start_date_set_at && (
                    <p className="text-xs text-muted-foreground">
                      Set on {format(new Date(details.sync_start_date_set_at), 'PPp')}
                    </p>
                  )}
                  <Button
                    variant="ghost"
                    size="sm"
                    className="text-destructive hover:text-destructive"
                    onClick={handleResetSyncDate}
                    disabled={savingSyncDate}
                  >
                    {savingSyncDate && <Loader2 className="mr-2 h-4 w-4 animate-spin" />}
                    Reset (sync all history)
                  </Button>
                </div>
              ) : (
                <p className="text-sm text-muted-foreground">
                  No sync start date configured — Fivetran is retrieving <span className="font-medium text-foreground">all historical data</span>, which may take several days for large datasets. Set a date below to sync only from that point forward.
                </p>
              )}

              {hasAmazonCsv && details?.sync_start_date_origin !== 'csv' && (
                <div className="space-y-2">
                  {!csvLastDate && (
                    <Button
                      variant="outline"
                      size="sm"
                      onClick={handleFetchCsvDate}
                      disabled={loadingCsvDate}
                    >
                      {loadingCsvDate && <Loader2 className="mr-2 h-4 w-4 animate-spin" />}
                      Fetch last CSV transaction date
                    </Button>
                  )}
                  {csvLastDate && (
                    <div className="flex items-center gap-2">
                      <span className="text-sm text-muted-foreground">CSV last date: {csvLastDate}</span>
                      <Button
                        size="sm"
                        onClick={() => handleSetSyncStartDate(csvLastDate, 'csv')}
                        disabled={savingSyncDate}
                      >
                        {savingSyncDate && <Loader2 className="mr-2 h-4 w-4 animate-spin" />}
                        Apply
                      </Button>
                    </div>
                  )}
                </div>
              )}

              <div className="space-y-2">
                <label className="text-sm font-medium">Manual Date Override</label>
                <div className="flex items-center gap-2">
                  <Popover>
                    <PopoverTrigger asChild>
                      <Button
                        variant="outline"
                        className={cn(
                          "w-[200px] justify-start text-left font-normal",
                          !manualDate && "text-muted-foreground"
                        )}
                      >
                        <CalendarIcon className="mr-2 h-4 w-4" />
                        {manualDate ? format(manualDate, 'PPP') : <span>Pick a date</span>}
                      </Button>
                    </PopoverTrigger>
                    <PopoverContent className="w-auto p-0" align="start">
                      <Calendar
                        mode="single"
                        selected={manualDate}
                        onSelect={setManualDate}
                        disabled={(date) => date > new Date()}
                        initialFocus
                        className={cn("p-3 pointer-events-auto")}
                      />
                    </PopoverContent>
                  </Popover>
                  <Button
                    size="sm"
                    onClick={() => {
                      if (manualDate) {
                        handleSetSyncStartDate(format(manualDate, 'yyyy-MM-dd'), 'manual');
                      }
                    }}
                    disabled={!manualDate || savingSyncDate}
                  >
                    {savingSyncDate && <Loader2 className="mr-2 h-4 w-4 animate-spin" />}
                    Apply Manual Date
                  </Button>
                </div>
              </div>
            </CardContent>
          </Card>
        )}

        {/* Tables */}
        {service !== 'google_sheets' && (
          <Card>
            <CardHeader className="pb-3">
              <CardTitle className="text-base">Tables</CardTitle>
            </CardHeader>
            <CardContent className="space-y-2">
              {loadingSchema ? (
                <div className="flex items-center gap-2 text-muted-foreground">
                  <Loader2 className="h-4 w-4 animate-spin" /> Loading schema...
                </div>
              ) : schemaChanges && Object.keys(schemaChanges).length > 0 ? (
                <>
                  {newlyDetectedTables.length > 0 ? (
                    <Alert className="border-amber-300 bg-amber-50 text-amber-900">
                      <AlertTitle>New tables detected</AlertTitle>
                      <AlertDescription className="space-y-1">
                        <p>
                          {newlyDetectedTables.length} new table{newlyDetectedTables.length === 1 ? '' : 's'} appeared since this connection was set up.
                          They stay disabled until explicitly enabled and saved.
                        </p>
                        <p className="text-xs">
                          {newlyDetectedTables.slice(0, 5).map((t) => t.tableName).join(', ')}
                          {newlyDetectedTables.length > 5 ? `, and ${newlyDetectedTables.length - 5} more` : ''}
                        </p>
                      </AlertDescription>
                    </Alert>
                  ) : null}
                  {Object.entries(schemaChanges).flatMap(([schemaName, schema]) =>
                    Object.entries(schema.tables || {}).map(([tableName, table]) => {
                      const displayTableName = tableName
                        .replace(/^[a-z0-9_]+\.\s*/i, '')
                        .replace(/^figabl_[a-z0-9]+_/i, '');
                      return (
                        <div key={`${schemaName}-${tableName}`} className="flex items-center gap-2 py-0.5 px-2">
                          <Checkbox
                            checked={table.enabled}
                            onCheckedChange={() => toggleTableEnabled(schemaName, tableName)}
                          />
                          <span className="text-sm">{displayTableName}</span>
                        </div>
                      );
                    })
                  )}
                  {Object.values(schemaChanges).every(s => Object.keys(s.tables || {}).length === 0) && (
                    <span className="text-xs text-muted-foreground">No tables available</span>
                  )}
                  <div className="flex items-center gap-2 mt-3">
                    <Button onClick={handleSaveSchema} disabled={savingSchema} size="sm">
                      {savingSchema && <Loader2 className="mr-2 h-4 w-4 animate-spin" />}
                      Save Table Config
                    </Button>
                    {/* <Button
                      variant="outline"
                      size="sm"
                      onClick={handleResetToDefaults}
                      disabled={loadingSchema || loadingDefaults || Object.keys(schemaChanges).length === 0}
                    >
                      <RotateCcw className="mr-2 h-4 w-4" />
                      Reset to defaults
                    </Button> */}
                  </div>
                </>
              ) : (
                <p className="text-sm text-muted-foreground">No schema data available</p>
              )}
            </CardContent>
          </Card>
        )}
      </DialogContent>
    </Dialog>
  );
};

export default OrgConnectionSettingsDialog;
